import { useEffect, useRef } from 'react'
import { updateAccountingPeriodStage } from '../redux/actions/accountingPeriodsActions'
import { useAppDispatch } from '../redux/hooks/reduxHooks'
import { ACCOUNTINGPERIODS_UPDATE_ERROR } from '../redux/slices/accountingPeriodsSlice'
import useApi from './useApi'
import useCleanup from './useCleanup'

function useCompleteAccountingStage(uploadState: number, onSuccess: () => void) {
	const api = useApi()
	const dispatch = useAppDispatch()
	const currentApi = useRef(api)
	currentApi.current = api
	const accountingPeriodId = useRef<string>()
	const pending = useRef<{ api: typeof api; controller: AbortController }>()

	useEffect(() => {
		if (pending.current && pending.current.api !== api) {
			pending.current.controller.abort()
			pending.current = undefined
			dispatch(ACCOUNTINGPERIODS_UPDATE_ERROR('Your access changed. Please retry completing this stage.'))
		}
	}, [api, dispatch])

	const complete = useCleanup(() => {
		let cancelled = false
		const controller = new AbortController()
		const run = async () => {
			if (currentApi.current !== api) {
				dispatch(ACCOUNTINGPERIODS_UPDATE_ERROR('Your access changed. Please retry completing this stage.'))
				return
			}
			if (!accountingPeriodId.current) {
				dispatch(ACCOUNTINGPERIODS_UPDATE_ERROR('Could not find the accounting period. Please try again.'))
				return
			}
			pending.current = { api, controller }
			const updated = await dispatch(updateAccountingPeriodStage(
				api, { accountingPeriodId: accountingPeriodId.current, uploadState }, { signal: controller.signal }
			))
			if (pending.current?.controller === controller) pending.current = undefined
			if (!cancelled && !controller.signal.aborted && currentApi.current === api && updated) onSuccess()
		}
		run()
		return () => {
			cancelled = true
			controller.abort()
			if (pending.current?.controller === controller) pending.current = undefined
		}
	})

	return (periodId: string | undefined) => {
		accountingPeriodId.current = periodId
		complete()
	}
}

export default useCompleteAccountingStage
